"use client";

import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMathVirtualKeyboardPadding } from "@/hooks/useMathVirtualKeyboardPadding";
import { usePathname } from "next/navigation";
import AuthPage from "@/components/Auth/AuthPage";
import { useAppDispatch, useAppSelector } from "@/store";
import type { RootState } from "@/store";
import { logout, setUser } from "@/store/slices/authSlice";
import { useGetUserQuery } from "@/api/auth/authPrivateApi";
import { Button } from "@/components/ui/button";
import { getHttpStatus } from "@/lib/apiErrorBag";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import AppRightSidebar from "@/components/AppRightSidebar";
import {
  AssignmentHeaderProvider,
  useAssignmentHeader,
} from "@/context/AssignmentHeaderContext";
import { useTranslations } from "next-intl";
import { isStudent as checkIsStudent } from "@/src/lib/auth";

const noSidebarPages = [
  "/assignments/testing",
  "/assignments/proctoring",
  "/debug/1",
  "/assignments/quiz",
  "/register",
  "/report",
  "/check/import-students",
  "/check/import-test",
  "/payment",
  "/session/assignment",
  "/proto",
];

const publicPages = [
  "/register",
  "/report",
  "/check/import-students",
  "/check/import-test",
  "/auth/azure/callback",
  "/auth-pdd",
  "/session/assignment",
  "/proto",
];

const transientErrorStatuses = [429, 502, 503, 504];

const matchesPrefix = (path: string, prefix: string) =>
  path === prefix || path.startsWith(prefix + "/");

const shouldShowSidebarFor = (path: string) =>
  !noSidebarPages.some((page) => matchesPrefix(path, page));

const isPublicRoute = (path: string) => publicPages.some((p) => matchesPrefix(path, p));

const AppHeaderWithAssignmentTitle: React.FC = () => {
  const { data } = useAssignmentHeader();
  return <AppHeader assignmentTitle={data.title} />;
};

const ConditionalLayout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state: RootState) => state.auth);

  // Prevent hydration mismatches: on the server, `usePathname()` can be empty/unstable.
  // We render a stable loading shell until the component is mounted on the client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  // Avoid setState-in-effect lint: compute token synchronously on first client render.
  // This component is `"use client"`, so this initializer runs in the browser.
  const [hasToken] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem("access_token");
  });

  // On the very first render (SSR + first client paint), avoid any auth calls.
  // After mount, we can safely use pathname-based routing.
  const publicRouteForQuery = !mounted ? true : isPublicRoute(pathname);

  const { data, isFetching, isError, error, refetch } = useGetUserQuery(undefined, {
    skip: publicRouteForQuery || !hasToken,
  });

  useEffect(() => {
    if (data) dispatch(setUser(data));
  }, [data, dispatch]);

  // Viewport lock: managers always render at desktop width (matching fix/before_mobile behaviour).
  // Keep the desktop-width viewport lock only for managers at desktop widths.
  useLayoutEffect(() => {
    if (!user) return;
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;

    const syncViewport = () => {
      const shouldForceDesktopViewport =
        !checkIsStudent(user) && window.innerWidth >= 769;

      if (shouldForceDesktopViewport) {
        viewport.setAttribute('content', 'width=768, initial-scale=1, maximum-scale=1');
        return;
      }

      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
    };
  }, [user]);

  // If user is already logged in and opens /register/*, automatically sign them out.
  // This ensures the public registration flow is always anonymous.
  const didAutoLogoutRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useMathVirtualKeyboardPadding(scrollContainerRef);
  useEffect(() => {
    if (!mounted) return;
    if (!publicRouteForQuery) return;
    if (matchesPrefix(pathname, "/session/assignment")) return;
    if (!hasToken) return;
    if (didAutoLogoutRef.current) return;

    didAutoLogoutRef.current = true;
    dispatch(logout());
  }, [dispatch, hasToken, mounted, pathname, publicRouteForQuery]);

  if (!mounted) {
    return (
      <div className="flex w-full h-screen flex-col items-center justify-center gap-4">
        <div className="h-16 w-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        <div className="text-gray-500 text-sm">{t("label-loading")}</div>
      </div>
    );
  }

  const publicRoute = isPublicRoute(pathname);
  const showSidebar = shouldShowSidebarFor(pathname);
  // На квизе прокручивается сам вопрос внутри страницы, поэтому обёртка не скроллится.
  const isQuizPage = pathname.includes("/quiz/");

  // Public routes should render even without a token (e.g. /register/[slug])
  if (publicRoute) {
    // For report and import-check pages, return bare content without any providers or authentication
    if (
      pathname.startsWith("/report/") ||
      pathname.startsWith("/session/assignment/") ||
      pathname.startsWith("/check/import-students") ||
      pathname.startsWith("/proto") ||
      pathname.startsWith("/auth/azure/callback") ||
      pathname === "/auth-pdd"
    ) {
      return <>{children}</>;
    }

    // For other public routes like /register, keep the existing wrapper
    return (
      <div className={`flex h-screen w-screen overflow-hidden ${showSidebar ? "bg-gray-50" : "bg-white"}`}>
        <SidebarProvider>
          <AssignmentHeaderProvider>
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className={`flex-1 bg-white ${isQuizPage ? "overflow-hidden" : "overflow-y-auto"}`}>
                <SidebarInset className={isQuizPage ? "min-h-0 h-full" : ""}>{children}</SidebarInset>
              </div>
            </div>
          </AssignmentHeaderProvider>
        </SidebarProvider>
      </div>
    );
  }

  const loading = isFetching;

  if (loading) {
    return (
      <div className="flex w-full min-h-svh flex-col items-center justify-center gap-4">
        <div className="h-16 w-16 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        <div className="text-gray-500 text-sm">{t("label-loading")}</div>
      </div>
    );
  }

  // Сеть, 429 или недоступный бэкенд — сессия жива: вместо формы входа предлагаем повторить.
  const errorStatus = getHttpStatus(error);

  if (isError && (errorStatus === null || transientErrorStatuses.includes(errorStatus))) {
    return (
      <div className="flex w-full min-h-svh flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">{t("ui-error-fetching-data")}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {t("btn-retry")}
        </Button>
      </div>
    );
  }

  if (!hasToken || isError || !isAuthenticated || !user) {
    return <AuthPage />;
  }

  return (
    <div
      className={`flex h-screen w-screen overflow-hidden ${showSidebar ? "bg-gray-50" : "bg-white"
        }`}
    >
      <SidebarProvider
        style={
          user?.school_id === 822
            ? ({ "--sidebar-width": "20rem" } as React.CSSProperties)
            : undefined
        }
      >
        <AssignmentHeaderProvider>
          {showSidebar && (
            <div className="flex-shrink-0">
              <AppSidebar />
            </div>
          )}

          <div className="flex-1 flex flex-col overflow-hidden">
            {showSidebar && (
              <div className="flex-shrink-0">
                <AppHeaderWithAssignmentTitle />
              </div>
            )}

            <div 
                ref={scrollContainerRef} 
                className={`flex-1 bg-white ${isQuizPage ? "overflow-hidden" : "overflow-y-auto"}`}
            >
              <SidebarInset className={isQuizPage ? "min-h-0 h-full" : ""}>{children}</SidebarInset>
            </div>
          </div>

          {showSidebar && (
            <div className="flex-shrink-0">
              <AppRightSidebar />
            </div>
          )}
        </AssignmentHeaderProvider>
      </SidebarProvider>
    </div>
  );
};
export default ConditionalLayout;
